import {AddressResponseData, ApiError, IAddressData, IPaymentData, PaymentFactory} from "@connox/typescript-library";
import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import CartDetails from "../../../cart/CartDetails/CartDetails";
import {Button, ButtonStyles, ButtonType} from "../../../lib/form/Button/Button";
import genericStyles from "../../../lib/GenericStyles.module.scss";
import {getShopId} from "../../../lib/getShopId";
import {IPaymentModule} from "../../../lib/payments/IPaymentModule";
import {PaymentController, Payments} from "../../../lib/payments/PaymentController";
import "../../../lib/SimpleRadioList.scss";
import ListLoader from "../../../utils/loader/ListLoader";
import Spinner from "../../../utils/loader/Spinner";
import {CheckoutError, DEFAULT_ERROR_MESSAGE, IIsVisible, prepareTrackingProductData} from "../../Checkout";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {IBraintreeError} from "../Summary/Summary";
import {captureSentryException} from "../../../../helpers/sentryHelper";
import PaymentRow from "./PaymentRow";
import {isSameAddress} from "../AddressStep/AddressStep";
import {useCustomerSelector} from "../../../../store";
import {Tracking} from "../../../../tracking/tracking";

interface IPaymentContext {
  creditCardImageRef: React.MutableRefObject<HTMLDivElement | null>;
  dateOfBirthRef: React.MutableRefObject<string | null>;
  isPaymentLoading: boolean;
}

interface IRequestState {
  buttonType: ButtonType;
  buttonValue: string | JSX.Element;
  errors: (string | JSX.Element)[];
  failedPaymentId?: number;
}

const paymentContext = React.createContext<IPaymentContext | null>(null);

export const usePaymentContext = (): IPaymentContext => {
  const context = React.useContext(paymentContext);
  if (!context) {
    throw new Error("not into component");
  }

  return context;
};

const Payment: React.FC<IIsVisible> = (props) => {
  const {customer} = useCustomerSelector();
  const {checkout} = useCheckoutContext();
  const {i18n, t} = useTranslation(["errorcodes", "translations"]);
  const [paymentOptions, setPaymentOptions] = useState<IPaymentData[]>([]);
  const [preferredPaymentId, setPreferredPaymentId] = useState<number>();
  const [paymentModule, setPaymentModule] = useState<IPaymentModule | null>(null);
  const isContinueInitiallyBlocked = !checkout.paymentId || checkout.paymentId === Payments.CreditCardBraintree;
  const buttonText = t("checkout.toSummary");
  const [requestState, setRequestState] = useState<IRequestState>({
    buttonType: isContinueInitiallyBlocked ? ButtonType.disabled : ButtonType.default,
    buttonValue: buttonText,
    errors: [],
  });
  // This ref is used in PaymentBirthdayPicker.tsx
  const dateOfBirthRef = useRef<string>(null);
  const creditCardImageRef = useRef<HTMLDivElement>(null);
  const previousDataRef = useRef<{
    deliveryAddress: AddressResponseData | null;
    invoiceAddress: AddressResponseData | null;
    oldPayableAmount: string;
  }>({
    deliveryAddress: null,
    invoiceAddress: null,
    oldPayableAmount: "null",
  });

  /**
   * request payment methods
   */
  const paymentIdsToBeFiltered = [100004, 100017, 100023, 100025, 100032];

  useEffect(() => {
    const {invoiceAddress, deliveryAddress, oldPayableAmount} = previousDataRef.current;
    const payableAmount = checkout.cartData.value.payable.amount;

    previousDataRef.current = {
      deliveryAddress: checkout.deliveryAddress,
      invoiceAddress: checkout.invoiceAddress,
      oldPayableAmount: payableAmount,
    };

    if (
      isSameAddress(invoiceAddress, checkout.invoiceAddress) &&
      isSameAddress(deliveryAddress, checkout.deliveryAddress) &&
      oldPayableAmount === payableAmount
    ) {
      return;
    }

    if (checkout.cartData.positions.find((pos) => pos.item?.itemClassId === 6) && !customer.isLoggedIn) {
      paymentIdsToBeFiltered.push(Payments.Invoice);
    }
    void PaymentFactory.getPaymentMethods(getShopId(), checkout.checkoutId).then((tempAvailable) => {
      const availables = tempAvailable.filter(
        (temp) => !paymentIdsToBeFiltered.includes(temp.paymentId) && temp.paymentAvailability.isAvailable,
      );

      const paymentInAvailables = availables.find(
        (available) =>
          available.paymentId === PaymentController.getPaymentId() || available.paymentId === checkout.paymentId,
      )?.paymentId;

      const tempPreferredPaymentId =
        availables.find((available) => available.isCustomerPreferredPayment)?.paymentId || Payments.PaypalBraintree;

      const paymentIdToSet = paymentInAvailables || tempPreferredPaymentId;

      setPreferredPaymentId(tempPreferredPaymentId);
      setPaymentModule(PaymentController.createPayment(checkout, paymentIdToSet));

      const availablesWithPayPalFirst = [
        ...availables.filter((available) => available.paymentId === Payments.PaypalBraintree),
        ...availables.filter((available) => available.paymentId !== Payments.PaypalBraintree),
      ];
      setPaymentOptions(availablesWithPayPalFirst);
    });
  }, [checkout.invoiceAddress, checkout.deliveryAddress, checkout.cartData.value.payable.amount]);

  /**
   * init inputs in payment row
   */
  useEffect(() => {
    if (!props.isVisible) {
      setRequestState((prevState) => ({...prevState, buttonValue: buttonText, errors: []}));

      return;
    }

    if (!(paymentModule && props.isVisible)) {
      return;
    }

    setRequestState((prevState) => ({...prevState, buttonType: ButtonType.disabled}));

    paymentModule
      .onPaymentSelect(creditCardImageRef)
      .then(() => setRequestState((prevState) => ({...prevState, buttonType: ButtonType.default})))
      .catch((err) => {
        const tempError = ((err as ApiError).originalError || err) as Error & IBraintreeError;
        // Credit Card is the only payment, that has a select functionality
        tempError.name = "CreditCard" + tempError.name;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
        let errorMessage: string = tempError.details?.originalError?.message || tempError.message;
        if (i18n.exists(errorMessage, {ns: "errorcodes"})) {
          errorMessage = t(errorMessage);
        } else {
          const sentryEventId = captureSentryException(tempError, checkout, "cc hosted fields init on selection");
          errorMessage = t(DEFAULT_ERROR_MESSAGE, {sentryEventId});
        }
        setRequestState((prevState) => ({
          ...prevState,
          buttonType: ButtonType.default,
          errors: [errorMessage],
        }));
      });
  }, [paymentModule, props.isVisible]);

  if (!props.isVisible) {
    return null;
  }

  if (!paymentOptions.length) {
    return (
      <div className={`Shipping ${genericStyles.mobileOnlyFriendly}`}>
        <h1>{t("checkout.paymentOptions")}</h1>
        <ListLoader />
      </div>
    );
  }

  const isButtonEnabled =
    (PaymentController.getPaymentId() || !!preferredPaymentId) && typeof requestState.buttonValue === "string";

  /**
   * payment clicked handler
   * @param pid
   */
  const onPaymentClicked = (pid: number) => {
    const oldPayment = PaymentController.getPayment();
    if (oldPayment && oldPayment.setAsDeselected) {
      oldPayment.setAsDeselected();
    }
    const newPayment = PaymentController.createPayment(checkout, pid);
    setRequestState((prevState) => ({...prevState, errors: []}));
    setPaymentModule(newPayment);
  };

  /**
   * Submit handler
   * @param evt
   */
  const submitPayment = async (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();

    const paymentId = PaymentController.getPaymentId();
    if (!(paymentId && paymentModule)) {
      return;
    }
    const submitErrors: string[] = [];
    setRequestState((prevState) => ({...prevState, buttonValue: <Spinner />}));
    let failedPaymentId: number | undefined;
    let addressWithBirthday: {delivery: IAddressData; invoice: IAddressData} | undefined;

    const currentPayment = paymentOptions.find((available) => available.paymentId === paymentId) as IPaymentData;

    if (currentPayment.isScoringRequired) {
      if (dateOfBirthRef.current === null || dateOfBirthRef.current === "") {
        submitErrors.push(t("form.pleaseEnterBirthday"));
      }
      if (!checkout.invoiceAddress) {
        submitErrors.push(t("checkout.emptyInvoiceAddress"));
      }
      if (submitErrors.length || !checkout.invoiceAddress) {
        setRequestState((prevState) => ({
          ...prevState,
          buttonValue: buttonText,
          errors: submitErrors,
        }));

        return;
      }
      const invoiceAddressWithBirthday = {...checkout.invoiceAddress, dateOfBirth: dateOfBirthRef.current};
      addressWithBirthday = {
        delivery: invoiceAddressWithBirthday,
        invoice: invoiceAddressWithBirthday,
      };
    }

    const paymentType = currentPayment.name;
    const productData = prepareTrackingProductData(checkout.cartData);
    Tracking.sendAddPaymentInfoEvent(paymentType, productData);

    let creditCardType = "";
    try {
      const submitPaymentResponse = await paymentModule.onSubmitPayment();
      if (submitPaymentResponse.err) {
        creditCardType = submitPaymentResponse.err.cardType;
        // Special case 'American Express' in .com: show alternate wording that says, it's only possible with 'Euro' as currency
        const context =
          creditCardType === "American Express" && getShopId() === 100005 ? "AMERICAN_EXPRESS" : undefined;
        setRequestState({
          ...requestState,
          buttonValue: buttonText,
          errors: [t(submitPaymentResponse.err.code ?? "", {creditCardType, context})],
        });

        return;
      }
      await checkout.put({
        paymentId,
        address: addressWithBirthday,
      });
    } catch (submitError) {
      let errorStringToPush = "";
      const tempNetworkError = ((submitError as ApiError).originalError || submitError) as {
        response?: {status: number};
        code?: string;
      } & Error;
      tempNetworkError.name = "CreditCard" + tempNetworkError.name;
      if (tempNetworkError.response?.status === 400) {
        errorStringToPush = "paymentMethodNotAvailable";
        failedPaymentId = paymentId;
      } else {
        errorStringToPush = tempNetworkError.code || tempNetworkError.message;
      }

      if (i18n.exists(errorStringToPush, {ns: "errorcodes"})) {
        submitErrors.push(t(errorStringToPush, {creditCardType}));
      } else {
        const sentryEventId = captureSentryException(tempNetworkError, checkout, "Submit payment catch block");
        submitErrors.push(t(DEFAULT_ERROR_MESSAGE, {sentryEventId}));
      }
    }
    setRequestState((prevState) => ({...prevState, buttonValue: buttonText, errors: submitErrors, failedPaymentId}));
  };

  const isPaymentLoading = requestState.buttonType === ButtonType.disabled;

  return (
    <div className={`Shipping Payment ${genericStyles.checkoutContainer} ${genericStyles.mobileOnlyFriendly}`}>
      <h1>{t("checkout.paymentOptions")}</h1>
      <CheckoutError>{requestState.errors}</CheckoutError>
      <form action="#">
        <div className="SimpleRadioList">
          <paymentContext.Provider value={{creditCardImageRef, dateOfBirthRef, isPaymentLoading}}>
            {paymentOptions.map((payment) => (
              <PaymentRow
                failedPaymentId={requestState.failedPaymentId}
                key={payment.paymentId}
                onPaymentClicked={onPaymentClicked}
                payment={payment}
              />
            ))}
          </paymentContext.Provider>
        </div>
        <CartDetails />
        <Button
          styles={[ButtonStyles.wide]}
          action={submitPayment}
          type={isButtonEnabled ? requestState.buttonType : ButtonType.disabled}
        >
          {requestState.buttonValue}
        </Button>
      </form>
    </div>
  );
};

export default Payment;
